import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { systemApi } from "../../lib/systemApi";
import { auth } from "../../lib/api";
import { SEOHeadV2 } from "../../components/SEOHeadV2";

const wrap = {
  minHeight: "100vh",
  background: "#0d1f3c",
  color: "#f4efe6",
  fontFamily: "Merriweather, Georgia, serif",
  padding: "60px 24px",
};
const inner = { maxWidth: 880, margin: "0 auto" };
const h1 = {
  fontFamily: "Cinzel, Trajan Pro, Georgia, serif",
  color: "#c9a84c",
  fontSize: 32,
  letterSpacing: 2.5,
  textTransform: "uppercase",
  marginBottom: 8,
};
const lede = {
  color: "rgba(244,239,230,0.7)",
  fontSize: 13,
  letterSpacing: 1,
  marginBottom: 28,
};
const card = {
  background: "#112244",
  border: "2px double #c9a84c",
  borderRadius: 8,
  padding: 24,
  marginBottom: 18,
};
const row = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  flexWrap: "wrap",
  gap: 16,
};
const label = {
  fontFamily: "Cinzel, Georgia, serif",
  fontSize: 12,
  color: "#c9a84c",
  letterSpacing: 1.4,
  textTransform: "uppercase",
};
const value = { fontSize: 13, color: "#f4efe6", fontFamily: "ui-monospace, Menlo, monospace" };
const btn = (active) => ({
  padding: "10px 24px",
  background: active ? "#c9a84c" : "transparent",
  color: active ? "#0d1f3c" : "#c9a84c",
  border: "1.5px solid #c9a84c",
  borderRadius: 4,
  fontFamily: "Cinzel, Georgia, serif",
  fontWeight: 600,
  letterSpacing: 1.2,
  textTransform: "uppercase",
  cursor: "pointer",
  fontSize: 12,
});

export const AdminSystem = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [me, setMe] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);

  const refresh = async () => {
    try {
      const s = await systemApi.status();
      setStatus(s);
    } catch (e) {
      setError("Failed to load system status.");
    }
  };

  useEffect(() => {
    if (!auth.token()) {
      navigate("/admin/login");
      return;
    }
    auth.me()
      .then((u) => {
        setMe(u);
        if (!["super_admin", "administrator"].includes(u.role)) {
          setError("Access denied. Insufficient permissions for this role.");
        }
      })
      .catch(() => navigate("/admin/login"));
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = async (next) => {
    if (busy) return;
    if (me?.role !== "super_admin") {
      setToast("Only super_admin may toggle DEMO_MODE.");
      return;
    }
    setBusy(true);
    try {
      await systemApi.setDemoMode(next);
      setToast(`DEMO_MODE ${next ? "enabled" : "disabled"}.`);
      await refresh();
    } catch (e) {
      const detail = e?.response?.data?.detail?.detail || e?.response?.data?.detail || "Toggle failed.";
      setToast(typeof detail === "string" ? detail : "Toggle failed.");
    } finally {
      setBusy(false);
      setTimeout(() => setToast(null), 3000);
    }
  };

  if (error) {
    return (
      <div style={wrap} data-testid="admin-system-page">
        <SEOHeadV2 title="System Settings — AuditIQ Admin" />
        <div style={inner}>
          <h1 style={h1}>System Settings</h1>
          <div style={{ ...card, borderColor: "#e74c3c", color: "#e74c3c" }} data-testid="admin-system-403">
            {error}
          </div>
          <Link to="/admin/login" style={{ color: "#c9a84c" }}>← Sign in as super_admin</Link>
        </div>
      </div>
    );
  }

  const flags = [
    { k: "system_status", l: "SYSTEM_STATUS" },
    { k: "public_access", l: "PUBLIC_ACCESS" },
    { k: "indexing_allowed", l: "INDEXING_ALLOWED" },
    { k: "canonical_base_url", l: "CANONICAL_BASE_URL" },
  ];

  return (
    <div style={wrap} data-testid="admin-system-page">
      <SEOHeadV2 title="System Settings — AuditIQ Admin" />
      <div style={inner}>
        <h1 style={h1} data-testid="admin-system-title">System Settings</h1>
        <div style={lede}>
          Production readiness flags — DEMO_MODE may be toggled live by super_admin.
        </div>

        <div style={card} data-testid="admin-system-demo-mode-card">
          <div style={row}>
            <div>
              <div style={label}>Demo Mode</div>
              <div style={{ ...value, marginTop: 4 }}>
                Effective:{" "}
                <span
                  style={{
                    color: status?.demo_mode ? "#f39c12" : "#2ecc71",
                    fontWeight: 700,
                  }}
                  data-testid="admin-system-demo-mode-effective"
                >
                  {status?.demo_mode ? "ON" : "OFF"}
                </span>
                {" · "}env default: {String(status?.demo_mode_env_default)}
                {status?.demo_mode_override !== null && status?.demo_mode_override !== undefined ? (
                  <span> · override: {String(status?.demo_mode_override)}</span>
                ) : null}
              </div>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button
                style={btn(status?.demo_mode === true)}
                onClick={() => toggle(true)}
                disabled={busy}
                data-testid="admin-system-demo-on"
              >
                Enable
              </button>
              <button
                style={btn(status?.demo_mode === false)}
                onClick={() => toggle(false)}
                disabled={busy}
                data-testid="admin-system-demo-off"
              >
                Disable
              </button>
            </div>
          </div>
        </div>

        <div style={card} data-testid="admin-system-flags-card">
          <div style={{ ...label, marginBottom: 12 }}>Production Flags</div>
          {flags.map((f) => (
            <div key={f.k} style={{ ...row, marginBottom: 8 }}>
              <div style={{ ...value, color: "#c9a84c" }}>{f.l}</div>
              <div style={value} data-testid={`admin-system-flag-${f.k}`}>
                {String(status?.[f.k] ?? "—")}
              </div>
            </div>
          ))}
        </div>

        {toast && (
          <div
            data-testid="admin-system-toast"
            style={{
              position: "fixed",
              bottom: 24,
              right: 24,
              background: "#112244",
              border: "1.5px solid #c9a84c",
              color: "#f4efe6",
              padding: "10px 16px",
              borderRadius: 6,
              fontSize: 13,
              zIndex: 9999,
            }}
          >
            {toast}
          </div>
        )}

        <Link to="/admin" style={{ color: "#c9a84c", marginTop: 16, display: "inline-block" }}>
          ← Back to Admin Hub
        </Link>
      </div>
    </div>
  );
};

export default AdminSystem;
